import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, CircularProgress, Chip } from "@material-ui/core";
import { useForm } from "react-hook-form";
import alternateCountryTranslations from "infrastructure/translate/alternateCountyTranslations";
import moment from "moment";
import Modal from "../Modal";
import useStyles from "./styles";
import { translate as t } from "../../../../infrastructure/translate/localization";
import CobeeButton from "../../CobeeButton";
import healthImg from "../../../../assets/img/health-insurance.jpg";
import CobeeTextField from "../../CobeeTextField";
import { MonetaryAmount } from "../../../domain/monetary-amount";

const OPTIONS = ["employee", "descendant", "partner"];

export default function HealthInsuranceModalV2({
  callback,
  data,
  isLoading,
  country,
}) {
  const {
    container,
    subtitle,
    healthLogo,
    productSwitchContainer,
    configContainer,
    productIsSelected,
    buttonBar,
    chipContainer,
    chip,
    activeChip,
    percentageAppliedRow,
    loaderContainer,
    linkText,
    rowSpacing,
    attachmentRow,
    scheludedMessage,
  } = useStyles();

  const [callbackIsLoading, setCallbackIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(0);

  const { register, handleSubmit, getValues, setValue, watch } = useForm();

  useEffect(() => {
    if (!isLoading) {
      data.insuranceConfig.forEach(
        ({ modalityId, subvention, allowedFamilyMembers }, i) => {
          register(`insuranceConfig.${i}.modalityId`);
          setValue(`insuranceConfig.${i}.modalityId`, modalityId);

          register(`insuranceConfig.${i}.allowedFamilyMembers`);
          setValue(
            `insuranceConfig.${i}.allowedFamilyMembers`,
            allowedFamilyMembers
          );

          OPTIONS.forEach((key) => {
            register(`insuranceConfig.${i}.subvention.${key}`);
            setValue(
              `insuranceConfig.${i}.subvention.${key}`,
              subvention[key] * 100
            );
          });
        }
      );
    }
  }, [register, data, setValue, isLoading]);

  const handleChipClick = ({ key }) => {
    const allowed = getValues(
      `insuranceConfig.${selectedProduct}.allowedFamilyMembers`
    );
    if (allowed.indexOf(key) === -1) {
      setValue(`insuranceConfig.${selectedProduct}.allowedFamilyMembers`, [
        ...allowed,
        key,
      ]);
    } else {
      setValue(
        `insuranceConfig.${selectedProduct}.allowedFamilyMembers`,
        allowed.filter((item) => item !== key)
      );
    }
  };

  const onSubmit = async (formData) => {
    setCallbackIsLoading(true);
    const parsedData = formData.insuranceConfig.map(
      ({
        modalityId,
        allowedFamilyMembers,
        subvention: { employee, descendant, partner },
      }) => {
        return {
          modalityId,
          allowedFamilyMembers,
          subvention: {
            employee: employee / 100,
            descendant: descendant / 100,
            partner: partner / 100,
          },
        };
      }
    );
    await callback({ payload: parsedData });
    setCallbackIsLoading(false);
  };

  return (
    <Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12} container className={container}>
          <Grid item xs={6}>
            <Typography variant="h6">
              {alternateCountryTranslations(country, "health-insurance")}
            </Typography>
            <Typography variant="h6" className={subtitle}>
              {t(
                "benefits-section.health-insurance-benefit.health-insurance-subsidy"
              )}
            </Typography>
          </Grid>

          <Grid item xs={6} container justifyContent="flex-end">
            <img src={healthImg} alt="health-logo" className={healthLogo} />
          </Grid>

          {isLoading ? (
            <Grid
              item
              container
              xs={12}
              justifyContent="center"
              className={loaderContainer}
            >
              <CircularProgress />
            </Grid>
          ) : (
            <Grid item xs={12} container>
              <Grid item xs={12} container>
                <Grid item xs={3}>
                  {data.insuranceProducts.map(({ modalityName, state }, i) => (
                    <Grid
                      item
                      xs={12}
                      key={`health-modality${String(i)}`}
                      className={`${productSwitchContainer} ${
                        selectedProduct === i ? productIsSelected : ""
                      }`}
                      onClick={() => setSelectedProduct(i)}
                    >
                      <>
                        <Typography>{modalityName}</Typography>
                        {state === "scheduled" && (
                          <Typography className={scheludedMessage}>
                            {t("common-messages.scheduled")}
                          </Typography>
                        )}
                      </>
                    </Grid>
                  ))}
                </Grid>

                <Grid
                  item
                  xs={9}
                  container
                  className={configContainer}
                  alignItems="center"
                >
                  <Grid item xs={6} className={rowSpacing}>
                    <Typography variant="h6">
                      {t("common-messages.price").toUpperCase()}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    container
                    justifyContent="flex-end"
                    alignItems="flex-end"
                    className={rowSpacing}
                  >
                    <Typography variant="h6">
                      {MonetaryAmount.ofCurrency(
                        data.insuranceProducts[selectedProduct].modalityFee
                      ).toUnitary()}
                      €
                    </Typography>
                    <Typography variant="body1">
                      /{t("common-messages.employee").toLowerCase()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t("common-messages.product-details")?.toUpperCase() ||
                        ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className={rowSpacing}>
                    <Typography variant="body1">
                      {
                        data.insuranceProducts[selectedProduct]
                          .modalityCoverageDescription
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t(
                        "common-messages.duration-of-the-policy"
                      )?.toUpperCase() || ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className={rowSpacing}>
                    <Typography variant="body1">
                      {`${moment(
                        data.insuranceProducts[selectedProduct]?.availableFrom
                      ).format("YYYY-MM-DD")} / ${moment(
                        data.insuranceProducts[selectedProduct]?.expiresAt
                      ).format("YYYY-MM-DD")}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t(
                        "common-messages.informative-attachments"
                      ).toUpperCase()}
                    </Typography>
                  </Grid>
                  <Grid item container xs={12} className={rowSpacing}>
                    {data.insuranceProducts[
                      selectedProduct
                    ].modalityBrochures.map(({ title, url }, i) => (
                      <Grid
                        item
                        xs={12}
                        container
                        key={`attachment-${String(i)}`}
                        className={attachmentRow}
                      >
                        <Grid item xs={6}>
                          <Typography variant="body1">{title}</Typography>
                        </Grid>
                        <Grid item xs={6} container justifyContent="flex-end">
                          <a
                            href={url}
                            className={linkText}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t("common-messages.see-attachment")}
                          </a>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      {t(
                        "benefits-section.wellness-benefit.who-will-benefit-message",
                        {
                          product:
                            data.insuranceProducts[selectedProduct]
                              .modalityName,
                        }
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} container className={chipContainer}>
                    {OPTIONS.map((key) => {
                      return (
                        <Chip
                          key={key}
                          label={t(`common-messages.${key}`)}
                          onClick={() => handleChipClick({ key })}
                          className={`${chip} ${
                            watch(
                              `insuranceConfig.${selectedProduct}.allowedFamilyMembers`
                            )?.indexOf(key) !== -1
                              ? activeChip
                              : ""
                          }`}
                        />
                      );
                    })}
                  </Grid>
                  <Grid item xs={12} container>
                    {OPTIONS.map((key) => {
                      if (
                        watch(
                          `insuranceConfig.${selectedProduct}.allowedFamilyMembers`
                        )?.indexOf(key) !== -1
                      ) {
                        return (
                          <Grid
                            key={key}
                            container
                            item
                            xs={12}
                            className={percentageAppliedRow}
                          >
                            <Grid
                              item
                              xs={8}
                              container
                              justifyContent="flex-start"
                            >
                              {t(
                                "benefits-section.wellness-benefit.company-will-cover-message",
                                { person: t(`common-messages.${key}`) }
                              )}
                            </Grid>
                            <Grid item xs={4} container alignItems="center">
                              <CobeeTextField
                                styleType="underlined"
                                value={watch(
                                  `insuranceConfig.${selectedProduct}.subvention.${key}`
                                )}
                                onChange={(e) =>
                                  setValue(
                                    `insuranceConfig.${selectedProduct}.subvention.${key}`,
                                    Number(e.target.value)
                                  )
                                }
                                type="number"
                                minWidth="80%"
                                min={0}
                                max={100}
                                step="0.01"
                              />
                              %
                            </Grid>
                          </Grid>
                        );
                      }
                      return null;
                    })}
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                container
                xs={12}
                justifyContent="flex-end"
                className={buttonBar}
              >
                <CobeeButton
                  type="submit"
                  isLoading={callbackIsLoading}
                  disabled={callbackIsLoading}
                >
                  {t("actions.save")}
                </CobeeButton>
              </Grid>
            </Grid>
          )}
        </Grid>
      </form>
    </Modal>
  );
}

HealthInsuranceModalV2.defaultProps = {
  data: {},
  isLoading: false,
  country: "ES",
};

HealthInsuranceModalV2.propTypes = {
  callback: PropTypes.func.isRequired,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  country: PropTypes.string,
};
